import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { ReportFieldType, ReportFilterOperator } from '../../models/report.model';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { DropdownFieldComponent } from '../../../pages/form-builder/fields/dropdown/view/dropdown-field.component';

/** Minimal shape the value editor needs to resolve how to render itself. */
export interface ResolvedFilterField {
  fieldType: ReportFieldType;
  options?: { label: string; value: string | number }[];
}

/** How the value cell renders for the current field + operator combination. */
type ValueMode = 'multi' | 'single-select' | 'number' | 'date' | 'boolean' | 'text';

/**
 * Formly FieldType for a report filter row's value cell.
 *
 * It inspects the sibling `field` and `operator` controls in the same row and
 * renders a type-appropriate editor:
 * - enum + `in`/`not_in` → a checklist of the field's options (`string[]`)
 * - enum + `eq`/`neq`     → a single-select of the field's options
 * - boolean               → a true/false select
 * - number / date         → a typed input
 * - everything else        → a text input (also used for `in`/`between` on
 *   non-enum fields, where the user types comma-separated values)
 *
 * The field's type + options are resolved via a `resolveField` callback supplied
 * in `props` by the report builder, so this component stays free of any
 * dependency on the builder's model.
 */
@Component({
  selector: 'app-report-filter-value',
  standalone: true,
  imports: [TranslatePipe, DropdownFieldComponent],
  templateUrl: './report-filter-value.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './report-filter-value.component.css',
})
export class ReportFilterValueComponent extends FieldType<FieldTypeConfig> {
  private translationService = inject(TranslationService);

  private get resolveField(): (ref: string) => ResolvedFilterField | undefined {
    const fn = this.props['resolveField'] as
      | ((ref: string) => ResolvedFilterField | undefined)
      | undefined;
    return fn ?? (() => undefined);
  }

  private get rowFieldRef(): string | undefined {
    return this.field.model?.['field'];
  }

  private get operator(): ReportFilterOperator | undefined {
    return this.field.model?.['operator'];
  }

  private get resolved(): ResolvedFilterField | undefined {
    const ref = this.rowFieldRef;
    return ref ? this.resolveField(ref) : undefined;
  }

  get fieldOptions(): { label: string; value: string | number }[] {
    return this.resolved?.options ?? [];
  }

  // --- Single-select / boolean modes render the same dropdown control used
  // for the row's field/operator cells, so the filter row looks consistent. ---

  /** Sub-field config for the enum single-select mode's `app-dropdown-field`. */
  get selectFieldConfig(): FieldTypeConfig {
    return {
      ...this.field,
      // Formly stores `formControl` as a non-enumerable property, so the
      // spread above silently drops it — carry it over explicitly or the
      // nested dropdown initializes with an undefined control.
      formControl: this.field.formControl,
      props: {
        // Dropdown option values must stay strings: coerceScalar() in
        // report-builder.model.ts calls .trim() on the raw filter value.
        options: this.fieldOptions.map((o) => ({ value: String(o.value), label: o.label })),
      },
    } as FieldTypeConfig;
  }

  /** Sub-field config for the boolean mode's `app-dropdown-field`. */
  get booleanFieldConfig(): FieldTypeConfig {
    return {
      ...this.field,
      formControl: this.field.formControl,
      props: {
        options: [
          {
            value: 'true',
            label: this.translationService.translate('reports.filters.boolean.true'),
          },
          {
            value: 'false',
            label: this.translationService.translate('reports.filters.boolean.false'),
          },
        ],
      },
    } as FieldTypeConfig;
  }

  get mode(): ValueMode {
    const fieldType = this.resolved?.fieldType;
    const op = this.operator;
    if (!op) return 'text';
    // Non-enum list / range operators keep the comma-separated text workflow.
    if (op === 'in' || op === 'not_in') return fieldType === 'enum' ? 'multi' : 'text';
    if (op === 'between') return 'text';
    if (fieldType === 'enum') return 'single-select';
    if (fieldType === 'number') return 'number';
    if (fieldType === 'date') return 'date';
    if (fieldType === 'boolean') return 'boolean';
    return 'text';
  }

  // --- Checklist (multi) mode -----------------------------------------------

  get selectedValues(): string[] {
    const value = this.formControl.value;
    return Array.isArray(value) ? value : [];
  }

  isChecked(value: string | number): boolean {
    return this.selectedValues.includes(String(value));
  }

  toggle(value: string | number): void {
    const key = String(value);
    const current = this.selectedValues;
    const next = current.includes(key) ? current.filter((v) => v !== key) : [...current, key];
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
  }

  // --- Scalar modes (select / number / date / boolean / text) ---------------

  get scalarValue(): string {
    const value = this.formControl.value;
    return Array.isArray(value) ? '' : (value ?? '');
  }

  setScalar(value: string): void {
    this.formControl.setValue(value);
    this.formControl.markAsDirty();
  }
}
